import { Component, input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AiToolCall } from '../ai-chat.config';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';

/**
 * Utility to clean up MCP responses for the UI
 */
export function safelyParseMcpPayload(payload: any): any {
  if (!payload) return payload;

  try {
    const payloadClone = JSON.parse(JSON.stringify(payload));

    if (Array.isArray(payloadClone.content)) {
      payloadClone.content.forEach((item: any) => {
        if (item.type === 'text' && typeof item.text === 'string') {
          try {
            item.text = JSON.parse(item.text);
          } catch (e) {
            // Not JSON, leave it as a string
          }
        }
      });
    }

    return payloadClone;
  } catch (e) {
    return payload;
  }
}

/**
 * @file chat-tool-call-card.component.ts
 * @description Renders a collapsible card visualizing a backend Agentic Tool Call (e.g. searching DB, making API requests).
 */
@Component({
  selector: 'rapider-chat-tool-call-card',
  standalone: true,
  imports: [CommonModule, RapiderJsonViewerComponent],
  template: `
    <div
      class="mt-2 mb-2 p-3 bg-component-inner border border-primary-border rounded-xl shadow-sm"
    >
      <div
        class="flex items-center justify-between cursor-pointer group"
        (click)="isExpanded.set(!isExpanded())"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-8 h-8 rounded-lg bg-alt-background border border-secondary-border flex items-center justify-center text-primary shadow-sm"
          >
            <i class="fas fa-wrench"></i>
          </div>
          <div class="flex flex-col min-w-0">
            <span class="text-xs font-bold text-primary-text truncate">{{
              toolCall().title || toolCall().name
            }}</span>
            <span
              class="text-[10px] text-secondary-text uppercase tracking-wider"
              >Tool Execution</span
            >
          </div>
        </div>
        <div class="flex items-center gap-3">
          <i
            class="fas"
            [ngClass]="{
              'fa-circle-notch fa-spin text-primary':
                toolCall().status === 'pending' || !toolCall().status,
              'fa-check-circle text-success': toolCall().status === 'success',
              'fa-times-circle text-danger': toolCall().status === 'error',
            }"
          ></i>
          <i
            class="fas transition-transform duration-200 text-secondary-text text-xs"
            [ngClass]="isExpanded() ? 'fa-chevron-down' : 'fa-chevron-right'"
          ></i>
        </div>
      </div>
      @if (isExpanded()) {
        <div
          class="mt-3 pt-3 border-t border-secondary-border animate-fade-in-up"
        >
          @if (toolCall().description) {
            <p class="text-sm text-secondary-text mb-4">
              {{ toolCall().description }}
            </p>
          }

          <span
            class="text-[10px] font-bold uppercase tracking-wider text-secondary-text mb-2 block"
            >Arguments (Input)</span
          >
          <pre
            class="text-xs text-primary-text bg-alt-background p-3 rounded-lg border border-secondary-border overflow-x-auto custom-scrollbar m-0 mb-4"
            >{{ getFormattedArgs() }}</pre
          >

          <span
            class="text-[10px] font-bold uppercase tracking-wider text-secondary-text mb-2 block"
            >Content (Output)</span
          >
          @if (toolCall().status === 'success') {
            <div
              class="bg-alt-background p-3 rounded-lg border border-secondary-border max-h-[400px] overflow-auto custom-scrollbar"
            >
              <rapider-json-viewer
                [data]="parsedResult()"
              ></rapider-json-viewer>
            </div>
          } @else if (toolCall().status === 'error') {
            <div
              class="bg-danger/10 border border-danger/20 text-danger p-3 rounded text-sm"
            >
              {{ toolCall().error || 'Unknown execution error' }}
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class ChatToolCallCardComponent {
  /** Backend AI Tool Call model input */
  public toolCall = input.required<AiToolCall>();

  /** Tracks whether the tool call details drawer is expanded or collapsed. Defaults to false (collapsed) */
  public isExpanded = signal<boolean>(false);

  /** Sanitized/parsed result payload for the JSON viewer */
  public parsedResult = computed(() => {
    return safelyParseMcpPayload(this.toolCall().result);
  });

  /**
   * Safely formats stringified JSON arguments into a readable tree.
   */
  public getFormattedArgs(): string {
    try {
      const args = this.toolCall().arguments;
      return typeof args === 'string'
        ? JSON.stringify(JSON.parse(args), null, 2)
        : JSON.stringify(args, null, 2);
    } catch {
      return String(this.toolCall().arguments);
    }
  }
}
